import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Container, Row, Col } from 'styled-bootstrap-grid';
import { MdKeyboardArrowRight } from 'react-icons/md';

import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';

import Header from '../../containers/Header';
import Footer from '../../containers/Footer';
import Breadcrumbs from '../../containers/Breadcrumbs';

import Card from '../../components/Card';
import Datatable from '../../components/Datatable';
import Button from '../../components/Button';
import DropdownButton from '../../components/DropdownButton';
import Badge from '../../components/Badge';
import Rating from '../../components/Rating';

type Color =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'warning'
  | 'info'
  | 'error';

interface StatusTypes {
  [key: string]: {
    ticket_title: string;
    color: Color;
  };
}

interface TicketData {
  ticket_id: string;
  ticket_title: string;
  student_id: string;
  student_first_name: string;
  student_last_name: string;
  category_name: string;
  ticket_status: string;
  ticket_rating: number;
  ticket_updated_at: string;
}

interface columnData {
  label: string;
  field:
    | 'ticket_id'
    | 'ticket_title'
    | 'ticket_status'
    | 'ticket_rating'
    | 'ticket_updated_at'
    | 'student_id'
    | 'student_first_name'
    | 'category_name';
  transform?(
    value: string | number,
    row?: TicketData,
  ): string | React.ReactElement;
}

const Ticket: React.FC = () => {
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [maxPages, setMaxPages] = useState(0);

  const [showingFrom, setShowingFrom] = useState(0);
  const [showingTo, setShowingTo] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [data, setData] = useState<TicketData[]>([]);

  const columns = useMemo<columnData[]>(
    () => [
      {
        label: 'Título',
        field: 'ticket_title',
      },
      {
        label: 'Categoria',
        field: 'category_name',
      },
      {
        label: 'Aluno',
        field: 'student_first_name',
        transform: (student_first_name, row: TicketData) => {
          return (
            <Link to={`/alunos/visualizar/${row.student_id}`}>
              {`${student_first_name} ${row.student_last_name}`}
            </Link>
          );
        },
      },
      {
        label: 'Última atualização',
        field: 'ticket_updated_at',
        transform: (updated_at: string) => {
          const formattedDate = format(
            parseISO(updated_at),
            "dd/MM/yyyy 'às' HH:mm",
            { locale: ptBR },
          );

          return formattedDate;
        },
      },
      {
        label: 'Avaliação',
        field: 'ticket_rating',
        transform: (rating: number) => {
          return rating ? (
            <Rating min={0} max={50} value={rating} readOnly />
          ) : (
            'Não avaliado'
          );
        },
      },
      {
        label: '',
        field: 'ticket_status',
        transform: (status: string) => {
          const statusTypes: StatusTypes = {
            pending: {
              ticket_title: 'Pendente',
              color: 'warning',
            },
            awaiting: {
              ticket_title: 'Aguardando',
              color: 'info',
            },
            closed: {
              ticket_title: 'Fechado',
              color: 'success',
            },
            canceled: {
              ticket_title: 'Cancelado',
              color: 'error',
            },
          };

          return (
            <Badge
              title={statusTypes[status].ticket_title}
              color={statusTypes[status].color}
            />
          );
        },
      },
      {
        label: '',
        field: 'ticket_id',
        transform: id => (
          <DropdownButton>
            <ul>
              <li>
                <Link to={`/chamados/visualizar/${id}`}>
                  <MdKeyboardArrowRight size={18} />
                  Visualizar
                </Link>
              </li>
            </ul>
          </DropdownButton>
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        const response = await api.get('/tickets', {
          params: { pageIndex, search },
        });

        setMaxPages(response.data.maxPages);
        setShowingFrom(response.data.showingFrom);
        setShowingTo(response.data.showingTo);
        setTotalRecords(response.data.totalRecords);
        setData(response.data.records);
      } catch (error) {
        // console.log(error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [pageIndex, search]);

  return (
    <>
      <Header />

      <Container style={{ flex: 1 }}>
        <Breadcrumbs title="Chamados" items={[{ title: 'Chamados' }]}>
          <Button
            color="secondary"
            onClick={() => history.push('/chamados/categorias')}
          >
            Categorias
          </Button>
        </Breadcrumbs>

        <Row>
          <Col md={12}>
            <Card>
              <Datatable
                loading={loading}
                columns={columns}
                showingTo={showingTo}
                showingFrom={showingFrom}
                totalRecords={totalRecords}
                pageIndex={pageIndex}
                maxPages={maxPages}
                setPageIndex={(value: number) => setPageIndex(value)}
                setSearch={(value: string) => setSearch(value)}
              >
                {data.map(row => (
                  <tr key={row.ticket_id}>
                    {columns.map(column => (
                      <td key={column.field}>
                        {column.transform
                          ? column.transform(row[column.field], row)
                          : row[column.field]}
                      </td>
                    ))}
                  </tr>
                ))}
                {!data.length && (
                  <tr>
                    <td colSpan={columns.length}>Nenhum registro encontrado</td>
                  </tr>
                )}
              </Datatable>
            </Card>
          </Col>
        </Row>
      </Container>

      <Footer />
    </>
  );
};

export default Ticket;
